import React, { useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const Slot = ({ label, bye, selected, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`w-full text-left px-3 py-2 text-sm rounded-md border transition-[background-color,border-color] ${
      selected ? 'border-[#CF142B] bg-red-50 ring-2 ring-[#CF142B]/30' : bye ? 'border-dashed border-slate-300 text-slate-400 bg-slate-50 hover:border-slate-400' : 'border-slate-200 bg-white hover:border-[#00247D]'}`}>
    {label}
  </button>
);

// Tap two spots to swap them: rearranges matchups and moves byes. Two byes can never meet.
export const MatchupEditor = ({ positions, teams, custom, onChange, onReset }) => {
  const [pick, setPick] = useState(null);
  if (!positions) return null;
  const seedOf = Object.fromEntries(teams.map((t, i) => [t.id, { seed: i + 1, name: t.name }]));
  const label = (id) => (id === 'BYE' ? 'BYE' : `${seedOf[id]?.seed}. ${seedOf[id]?.name}`);
  const tap = (i) => {
    if (pick === null) { setPick(i); return; }
    if (pick === i) { setPick(null); return; }
    const next = [...positions];
    [next[pick], next[i]] = [next[i], next[pick]];
    setPick(null);
    for (let g = 0; g < next.length; g += 2) {
      if (next[g] === 'BYE' && next[g + 1] === 'BYE') { toast.error("Two byes can't face each other", { description: 'Move a team into that game instead.' }); return; }
    }
    onChange(next);
  };
  const pairs = [];
  for (let g = 0; g < positions.length; g += 2) pairs.push(g);
  return (
    <div className="space-y-3" data-testid="bracket-matchups">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-slate-600 max-w-md">Tap one spot, then another, to swap them. Use it to rearrange first-round games or move a bye to a different team.</p>
        {custom && <Button type="button" variant="outline" size="sm" onClick={onReset} data-testid="bracket-matchups-reset-btn"><RotateCcw className="w-4 h-4 mr-1.5" />Standard seeding</Button>}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto pr-1">
        {pairs.map((g) => (
          <div key={g} className="rounded-lg bg-slate-50 border border-slate-200 p-2 space-y-1.5" data-testid={`bracket-matchup-${g / 2 + 1}`}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Line {g + 1} – {g + 2}</p>
            {[g, g + 1].map((i) => (
              <Slot key={i} label={label(positions[i])} bye={positions[i] === 'BYE'} selected={pick === i} onClick={() => tap(i)} testId={`bracket-line-${i + 1}`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
